import React, { useState, useEffect } from "react";
import { useAppContext } from "../contexts/AppContext";
import { fetchUserBookings } from "../api-client"; // Import your existing function
import { useNavigate } from "react-router-dom";

export interface Booking {
    responseToken: string;
    reference: string;
    subject: string;
    tripStartDate: string;
    tripEndDate: string;
    bookedDate: string;
    holidayType: string;
    primaryGuestName: string;
    status: string;
    bookingStatus: string;
    remarks: string;
    grandTotal: {
        currencyCode: string;
        amount: number;
    }[];
    userId: string;
    holdOrPaid: string;
    paidAmount: string;
    createdAt: string;
    email: string;
}

const MyBookings: React.FC = () => {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAppContext();
    const navigate = useNavigate();

    const userId = user?.userId;
    const isAdmin = user?.isAdmin ?? false;

    const formatDateToUS = (dateString: string) => {
        const date = new Date(dateString);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);

    useEffect(() => {
        const getBookings = async () => {
            if (userId) {
                try {
                    setIsLoading(true);

                    // Simulate API call delay
                    const data = await fetchUserBookings(userId, isAdmin);
                    setTimeout(() => {
                        setBookings(data);
                        setIsLoading(false);
                    }, 1500); // 1.5-second delay
                } catch (error: any) {
                    console.error("Error fetching user bookings:", error);
                    setTimeout(() => {
                        setError(
                            error?.message || "Failed to fetch bookings. Please try again later."
                        );
                        setIsLoading(false);
                    }, 1500); // 1.5-second delay
                }
            }
        };

        getBookings();
    }, [userId, isAdmin]);


    const handleViewBooking = (responseToken: string, holdOrPaid: string) => {
        navigate(`/booking-details/${responseToken}`, { state: { holdOrPaid } });
    };

    return (
        <div className="container mx-auto p-4 mt-16">
            <h1 className="text-3xl font-bold mb-6 text-center text-gray-700">
                My Bookings
            </h1>

            {isLoading ? (
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-500"></div>
                    <p className="ml-4 text-gray-600">Loading your bookings...</p>
                </div>
            ) : error ? (
                <p className="text-red-500 text-center">{error}</p>
            ) : bookings.length === 0 ? (
                <p className="text-gray-600 text-center">No bookings found.</p>
            ) : (
                <div>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full bg-white border rounded-lg shadow-md">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Reference
                                    </th>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Hotel
                                    </th>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Status
                                    </th>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Trip Dates
                                    </th>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Booking Date
                                    </th>
                                    <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">
                                        Total Amount
                                    </th>
                                    <th className="py-3 px-4 border-b text-left"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings
                                    .filter((booking) => {
                                        if (isAdmin) return true;
                                        return booking.holdOrPaid !== "pending";
                                    })
                                    .slice()
                                    .reverse()
                                    .map((booking) => (
                                        <tr key={booking.responseToken} className="hover:bg-gray-50 text-sm">
                                            <td className="py-3 px-4 border-b text-gray-600">
                                                {booking.reference}
                                            </td>
                                            <td className="py-3 px-4 border-b text-gray-600 font-semibold">
                                                {booking.subject}
                                            </td>
                                            <td
                                                className={`py-3 px-4 border-b font-medium ${booking.bookingStatus.toLowerCase() === "confirmed"
                                                        ? "text-green-600"
                                                        : booking.bookingStatus.toLowerCase() === "cancelled"
                                                            ? "text-red-600"
                                                            : booking.bookingStatus.toLowerCase() === "pending"
                                                                ? "text-yellow-600"
                                                                : "text-gray-600"
                                                    }`}
                                            >
                                                {booking.bookingStatus.charAt(0).toUpperCase() + booking.bookingStatus.slice(1)}
                                            </td>
                                            <td className="py-3 px-4 border-b text-gray-600">
                                                {formatDateToUS(booking.tripStartDate)} - {formatDateToUS(booking.tripEndDate)}
                                            </td>
                                            <td className="py-3 px-4 border-b text-gray-600">
                                                {booking.bookedDate}
                                            </td>
                                            <td className="py-3 px-4 border-b text-gray-800 font-semibold">
                                                {booking.grandTotal.length > 0 && (
                                                    <>
                                                        GBP{" "}
                                                        {(booking.grandTotal[0].amount * 1.04).toFixed(2)}
                                                    </>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <button
                                                    onClick={() =>
                                                        handleViewBooking(booking.responseToken, booking.holdOrPaid)
                                                    }
                                                    className="bg-pink-500 text-white px-4 py-2 rounded shadow hover:bg-pink-700 transition"
                                                >
                                                    View Booking
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="block md:hidden">
                        {bookings
                            .filter((booking) => {
                                if (isAdmin) return true;
                                return booking.holdOrPaid !== "pending";
                            })
                            .slice()
                            .reverse()
                            .map((booking) => (
                                <div
                                    key={booking.responseToken}
                                    className="bg-white border border-gray-200 rounded-lg shadow-md p-4 mb-6"
                                >
                                    {/* Reference and Subject */}
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm">Reference:</p>
                                        <p className="text-gray-700 font-medium text-base">{booking.reference}</p>
                                    </div>
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm">Hotel:</p>
                                        <p className="text-gray-700 font-medium text-base">{booking.subject}</p>
                                    </div>
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm">Status:</p>
                                        <p className={`font-medium text-base ${booking.bookingStatus.toLowerCase() === "confirmed"
                                                ? "text-green-600"
                                                : booking.bookingStatus.toLowerCase() === "cancelled"
                                                    ? "text-red-600"
                                                    : booking.bookingStatus.toLowerCase() === "pending"
                                                        ? "text-yellow-600"
                                                        : "text-gray-700"
                                            }`}>{booking.bookingStatus.charAt(0).toUpperCase() + booking.bookingStatus.slice(1)}</p>
                                    </div>

                                    {/* Trip Dates */}
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm">Trip Dates:</p>
                                        <p className="text-gray-700 font-medium text-base">
                                            {formatDateToUS(booking.tripStartDate)} - {formatDateToUS(booking.tripEndDate)}
                                        </p>
                                    </div>

                                    {/* Booking Date */}
                                    <div className="mb-4">
                                        <p className="text-gray-500 text-sm">Booking Date:</p>
                                        <p className="text-gray-700 font-medium text-base">{booking.bookedDate}</p>
                                    </div>

                                    {/* Total Amount */}
                                    <div className="mb-6">
                                        <p className="text-gray-500 text-sm">Total Amount:</p>
                                        <p className="text-pink-600 font-bold text-xl">
                                            {booking.grandTotal.length > 0 && (
                                                <>
                                                    GBP{" "}
                                                    {(booking.grandTotal[0].amount * 1.04).toFixed(2)}
                                                </>
                                            )}
                                        </p>
                                    </div>

                                    {/* View Booking Button */}
                                    <div className="text-right">
                                        <button
                                            onClick={() =>
                                                handleViewBooking(booking.responseToken, booking.holdOrPaid)
                                            }
                                            className="bg-pink-500 text-white px-6 py-2 rounded-md shadow-md hover:bg-pink-700 transition"
                                        >
                                            View Booking
                                        </button>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyBookings;
